import { useEffect, useState } from "react";
import { Users2, Loader2 } from "lucide-react";

const BASE_URL = import.meta.env.BASE_URL.replace(/\/$/, "");

type Accent = "teal" | "indigo" | "violet" | "emerald" | "amber";

interface ProfileSummary {
  teamCode?: string | null;
  organisationName?: string | null;
  siteName?: string | null;
}

const ACCENT: Record<Accent, { bg: string; text: string; ring: string; pill: string }> = {
  teal: { bg: "bg-teal-50", text: "text-teal-700", ring: "ring-teal-200", pill: "bg-teal-100 text-teal-800" },
  indigo: { bg: "bg-indigo-50", text: "text-indigo-700", ring: "ring-indigo-200", pill: "bg-indigo-100 text-indigo-800" },
  violet: { bg: "bg-violet-50", text: "text-violet-700", ring: "ring-violet-200", pill: "bg-violet-100 text-violet-800" },
  emerald: { bg: "bg-emerald-50", text: "text-emerald-700", ring: "ring-emerald-200", pill: "bg-emerald-100 text-emerald-800" },
  amber: { bg: "bg-amber-50", text: "text-amber-700", ring: "ring-amber-200", pill: "bg-amber-100 text-amber-900" },
};

export function TeamCodeCard({ accent }: { accent: Accent }) {
  const [profile, setProfile] = useState<ProfileSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${BASE_URL}/api/user/profile-summary`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data) => { if (!cancelled) { setProfile(data); setLoading(false); } })
      .catch(() => { if (!cancelled) { setError("Could not load your team code."); setLoading(false); } });
    return () => { cancelled = true; };
  }, []);

  const styles = ACCENT[accent];

  return (
    <section className="bg-white rounded-2xl border border-slate-200 p-8 space-y-3">
      <div className="flex items-center gap-2">
        <Users2 className={`h-5 w-5 ${styles.text}`} />
        <h3 className="font-bold text-slate-900">Team code</h3>
      </div>
      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : error ? (
        <p className="text-sm text-red-600">{error}</p>
      ) : profile?.teamCode ? (
        <>
          <div className={`flex flex-wrap items-center gap-3 rounded-xl ${styles.bg} ring-1 ${styles.ring} px-4 py-3`}>
            <span className={`inline-flex items-center font-mono text-lg font-bold tracking-wide rounded-md px-2.5 py-1 ${styles.pill}`}>
              {profile.teamCode}
            </span>
            <span className="text-sm text-slate-700">
              {profile.siteName || profile.organisationName || "Your team"}
            </span>
          </div>
          <p className="text-xs text-slate-500 leading-relaxed">
            This is the code that identifies your team to patients and colleagues on RheumHub. It is derived from your NHS organisation and site (ODS code).
          </p>
        </>
      ) : (
        <div className="rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-600">
          No team code yet — complete your NHS organisation and site in onboarding to receive one.
        </div>
      )}
    </section>
  );
}
